/**
 * Home Dashboard (Redesigned)
 *
 * Action-oriented dashboard that shows:
 * - Personalized greeting
 * - Case lifecycle stage pipeline
 * - Action cards for items needing attention
 * - Case overview stats
 * - Recent activity feed
 *
 * This replaces the tile-only dashboard with a guided experience
 * that shows users what to do next.
 */

import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserProfileContext from '../context/userprofilecontext';
import AIAssistantContext from '../context/aiassistantcontext';
import { useDashboardCaseStats } from '../api/casestats';
import { useGetAccountSummaries } from '../api/bankaccounts';
import { findOrCreateTopicSession, findOrCreateInterviewSession, routeToNextHilSession } from '../api/chat';
import { formatCurrency } from '../utils/formatters';
import {
  PageHeader,
  StagePipeline,
  ActionCard,
  StatCard,
  SectionTitle,
} from '../design-system';
import NetWorthChart from '../components/charts/NetWorthChart';
import CaseDatabaseCheck from '../components/ui/case-database-check';
import TopicsPanel from '../components/dashboard/TopicsPanel';

// Attention card icons — sourced from MUI to match the left-nav icon set
// (navigationConfig.js). Keeping icons consistent across surfaces is the rule;
// the nav is the source of truth for icon-per-feature.
import DescriptionIcon from '@mui/icons-material/Description';
import ForumIcon from '@mui/icons-material/Forum';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import BalanceIcon from '@mui/icons-material/Balance';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CategoryIcon from '@mui/icons-material/Category';
import FlagIcon from '@mui/icons-material/Flag';

import styles from './page-home-redesign.module.css';

/* ---- Helpers ---- */
function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function formatNumber(num) {
  if (num == null) return '—';
  return Number(num).toLocaleString();
}

function formatDollar(num) {
  if (num == null) return '—';
  return `$${formatCurrency(num)}`;
}

/* ---- Net Worth Data Builder ---- */
const LIABILITY_TYPES = ['credit', 'credit card', 'line of credit'];

function isLiabilityAccount(account) {
  // Fold the enum code to space-separated lowercase so canonical codes match
  // (e.g. "LINE_OF_CREDIT" -> "line of credit"). CREDITCARD -> "creditcard"
  // still matches via the "credit" token.
  const type = (account.account_type || '').toLowerCase().replace(/_/g, ' ');
  return LIABILITY_TYPES.some((t) => type.includes(t));
}

function buildNetWorthTimeline(accounts) {
  if (!accounts || !Array.isArray(accounts)) return null;

  // Backend already signs balance_history per expense_factor, so use it as-is
  const perAccount = accounts
    .filter((a) => a.balance_history && a.balance_history.length > 0)
    .map((a) => {
      return [...a.balance_history]
        .filter((p) => p.date)
        .sort((x, y) => x.date.localeCompare(y.date))
        .map((p) => ({ date: p.date, value: p.balance ?? 0 }));
    })
    .filter((h) => h.length > 0);

  if (perAccount.length === 0) return null;

  const allDates = Array.from(
    new Set(perAccount.flatMap((h) => h.map((p) => p.date)))
  ).sort((a, b) => a.localeCompare(b));

  const result = [];
  for (const date of allDates) {
    let total = 0;
    let anyActive = false;
    for (const history of perAccount) {
      let last = null;
      for (const point of history) {
        if (point.date <= date) last = point;
        else break;
      }
      if (last !== null) {
        total += last.value;
        anyActive = true;
      }
    }
    if (anyActive) result.push({ date, balance: total });
  }

  return result.length >= 2 ? result : null;
}

/* ---- Attention item mapping ----
 * The backend (case_stats.py::get_dashboard_workflow_summary) emits a merged
 * list of seeded high-priority punch list items + virtual reactive items
 * (HIL / uncategorized / flagged). The frontend just maps them to ActionCard
 * props. Click handlers branch on `click_target.type`.
 *
 * Priority/variant mapping is local to the dashboard — we keep it here so
 * the backend stays focused on data and the visual treatment lives with
 * the rendering surface.
 */
const PRIORITY_TO_PROPS = {
  CRITICAL: { priority: 'high', variant: 'error' },
  HIGH:     { priority: 'high', variant: 'warning' },
  MEDIUM:   { priority: 'medium', variant: 'info' },
  LOW:      { priority: 'low', variant: 'info' },
};

/* Icons for the well-known seeded + virtual attention items. Keyed off the
 * attention item's `key` (its seed_key or virtual.* identifier).
 *
 * For items that also appear in the left-nav (DRFA, MBS, Upload Files,
 * Categories), the icon MATCHES navigationConfig.js — same MUI icon, so
 * the user sees a consistent visual identity across the dashboard card
 * and the nav entry that opens the same surface.
 *
 * Self-extending: an unknown key falls through to a colored placeholder. */
const ATTENTION_ICONS = {
  // Setup → Provide Case Details (matches nav: Case Details)
  'stages.setup.children.provide_case_details': <DescriptionIcon fontSize="small" />,
  // Setup → Conduct case interview (no nav counterpart — sensible default)
  'stages.setup.children.case_interview': <ForumIcon fontSize="small" />,
  // Documents → Begin Uploading Documents (matches nav: Upload Files)
  'stages.documents.children.begin_upload': <UploadFileIcon fontSize="small" />,
  // Reports → Build Financial Affidavit (matches nav: Financial Affidavit)
  'stages.reports.children.financial_affidavit': <BalanceIcon fontSize="small" />,
  // Reports → Build Marital Balance Sheet (matches nav: Marital Balance Sheet (MBS)
  // — keeps the icon tied to the conceptual product even though the click
  // navigates to Manage MBS Accounts as the practical entry point)
  'stages.reports.children.marital_balance_sheet': <StorefrontIcon fontSize="small" />,
  // Virtual: Pending HIL scan reviews (no nav counterpart)
  'virtual.pending_hil': <PendingActionsIcon fontSize="small" />,
  // Virtual: Uncategorized transactions (matches nav: Categories)
  'virtual.uncategorized': <CategoryIcon fontSize="small" />,
  // Virtual: Flagged transactions (no nav counterpart)
  'virtual.flagged': <FlagIcon fontSize="small" />,
};

function mapAttentionItem(item, navigate, openChatSession, openTopic, openInterview, openHil) {
  const target = item.click_target || {};
  const onClick = () => {
    if (target.type === 'navigate' && target.path) {
      navigate(target.path);
    } else if (target.type === 'open_chat') {
      // Route server-side (openHil): picks the right session for THIS reviewer
      // and flips it PENDING -> ACTIVE, so two reviewers don't land on the same
      // unstarted scan. Falls back to a direct open if no router was provided.
      if (openHil) {
        openHil(item.key || item.item_id, target.session_id);
      } else if (openChatSession) {
        openChatSession(target.session_id || undefined);
      }
    } else if (target.type === 'open_topic' && target.topic_id) {
      // Phase 2 Topic Curation — find-or-create the dedicated chat session
      // for this topic and switch the AI Assistant to it. Same pattern the
      // dashboard Topics panel uses via handleTopicClick. Each topic gets
      // its own thread so investigations stay scoped.
      if (openTopic) {
        openTopic(target.topic_id, target.topic_title);
      }
    } else if (target.type === 'open_interview') {
      // Phase 3 Case Interview — find-or-create the caller's per-user
      // CASE_INTERVIEW session and switch to it. No payload needed; the
      // backend uses the cognitoUserID from the auth context.
      if (openInterview) {
        openInterview();
      }
    }
  };

  const { priority, variant } = PRIORITY_TO_PROPS[item.priority] || PRIORITY_TO_PROPS.MEDIUM;
  const icon = ATTENTION_ICONS[item.key];

  return {
    key: item.key || item.item_id,
    title: item.title,
    description: item.description,
    priority,
    variant,
    icon,
    actionLabel: target.type === 'open_chat' ? 'Resume' : 'Open',
    onClick,
  };
}



/* ---- Main Dashboard ---- */
export default function HomeDashboard() {
  const { userProfile } = useContext(UserProfileContext);
  const { canResolveHIL, canUseAIAssistant, openChatSession } = useContext(AIAssistantContext);
  // Cached-first: paint last-known stats (stages, counts, attention, topics) from
  // the primary-DB snapshot immediately, then upgrade to live once /case_stats
  // resolves against the just-woken case DB. `caseStats` is undefined only on a
  // brand-new case with no snapshot yet OR the very first moment before either
  // call returns — that's when we show skeletons instead of an empty frame.
  const { data: caseStats, mutate: refreshStats, isCachedOnly } = useDashboardCaseStats();
  const navigate = useNavigate();

  const hasCase = !!userProfile?.currentCase;
  const caseName = userProfile?.currentCase?.caseName;
  const hasTransactions = caseStats?.transaction_count > 0;

  // Fetch account summaries for the net worth chart (only when we have transactions)
  const { data: accountSummaries } = useGetAccountSummaries(hasCase && hasTransactions);

  const firstName = userProfile?.currentUser?.firstName
    || userProfile?.currentUser?.email?.split('@')[0]
    || 'there';

  // Stages and attention items are now driven by case-scope punch list items,
  // computed in case_stats.py::get_dashboard_workflow_summary. The dashboard
  // is no longer hardcoded — adding a new stage or task to the divorce seed
  // template surfaces it here automatically.
  const stages = caseStats?.stages || [];
  const topics = caseStats?.topics || [];
  // Discovery activity indicator for the Topics panel — null on legacy
  // backends that don't emit the field (older case_stats lambda); the
  // panel hides the chip in that case so the dashboard stays clean.
  const discoveryStatus = caseStats?.discovery_status || null;
  const keyDates = caseStats?.key_dates || [];
  const [showKeyDates, setShowKeyDates] = useState(true);

  const attentionItems = caseStats?.attention_items || [];
  // canResolveHIL gates the HIL-related virtual reactive item — filter it
  // out for non-resolvers so we don't surface a card they can't act on.
  // case_interview is now backend-gated by the case_interview skill (only
  // surfaces for users who can use chat); this is defense-in-depth so a
  // stale poll-cache or backend-skill-table drift can't surface an
  // un-actionable card to a user without chat access.
  const filteredAttention = useMemo(
    () => attentionItems.filter((item) => {
      if (item?.click_target?.type === 'open_chat' && !canResolveHIL) {
        return false;
      }
      if (item?.click_target?.type === 'open_interview' && !canUseAIAssistant) {
        return false;
      }
      // Same rule for navigate targets: the punch list is seeded per case-type,
      // not per role, so it offers every user the same CTAs — including pages
      // their role can't open. A case Viewer got "Begin Uploading Documents"
      // (-> /manage_uploaded_files, which Viewers don't have) and landed on a
      // 404 (Trello TYTHVoW2). Same hole on /manage_accounts (Marital Balance
      // Sheet) and /case_maintenance_sheet (Provide Case Details). Drop the
      // card rather than dangle an action they can't take — AccessDeniedPage
      // is the safety net for links reached some other way, not the plan.
      if (item?.click_target?.type === 'navigate'
          && item.click_target.path
          && userProfile
          && !userProfile.canAccess(item.click_target.path)) {
        return false;
      }
      return true;
    }).map((item) => {
      // The instant cached render comes from the DAILY :CaseStats snapshot,
      // which bakes a count into this card's title. The HIL queue drains
      // continuously, so that number is wrong by design, not just stale on a
      // bad day — on a prod case the 05:02 snapshot rendered "1 Scan Review
      // Waiting" while the table actually held 91. Keep the card visible (the
      // reviewer needs to know work exists, and the live call is the slow one)
      // but drop the number rather than show one we already know is wrong.
      // Live data restores the exact count a moment later.
      if (isCachedOnly && item?.key === 'virtual.pending_hil') {
        return { ...item, title: 'Scan Reviews Waiting' };
      }
      return item;
    }),
    [attentionItems, canResolveHIL, canUseAIAssistant, isCachedOnly, userProfile],
  );
  // Phase 2 Topic Curation click-through. Each topic gets a dedicated chat
  // session (find-or-create on first click) so the agent's reasoning stays
  // scoped to one hypothesis at a time and the user can return to a topic
  // without it being mixed with unrelated conversation. No protocol message,
  // no internal IDs in chat history — the find-or-create endpoint also
  // bootstraps the agent so the topic detail renders proactively on the
  // first turn of a new session.
  //
  // openingTopicRef prevents rapid double-clicks from racing two POSTs to
  // /chat/topic-session before the first one inserts a session row — the
  // backend deterministic pointer item is the structural defense against
  // duplicates, this is a UX-side belt-and-suspenders so the second click
  // doesn't even fire.
  const openingTopicRef = useRef(false);
  const handleTopicClick = useCallback(async (topicId /*, topicTitle */) => {
    if (!openChatSession || !topicId || openingTopicRef.current) return;
    openingTopicRef.current = true;
    try {
      const result = await findOrCreateTopicSession(topicId);
      const sessionID = result?.sessionID;
      if (sessionID) openChatSession(sessionID);
    } catch (err) {
      console.error('Failed to open topic session:', err);
    } finally {
      openingTopicRef.current = false;
    }
  }, [openChatSession]);

  // Phase 3 Case Interview click-through. Mirrors handleTopicClick — atomic
  // find-or-create on the backend (deterministic pointer keyed by (case, user))
  // is the structural defense against duplicates; the ref-guard is a UX-side
  // belt-and-suspenders so a rapid double-click doesn't fire the second POST.
  const openingInterviewRef = useRef(false);
  const handleInterviewClick = useCallback(async () => {
    if (!openChatSession || openingInterviewRef.current) return;
    openingInterviewRef.current = true;
    try {
      const result = await findOrCreateInterviewSession();
      const sessionID = result?.sessionID;
      if (sessionID) openChatSession(sessionID);
    } catch (err) {
      console.error('Failed to open interview session:', err);
    } finally {
      openingInterviewRef.current = false;
    }
  }, [openChatSession]);

  // HIL review click-through. The attention card bakes in a session_id from the
  // last /case_stats poll, and re-resolving it client-side had two problems: a
  // session that resolved between the poll and the click opened a dead
  // conversation, and — worse — EVERY reviewer re-resolved to the same
  // oldest-pending id, so they all landed on the same unstarted scan and
  // silently duplicated each other's work.
  //
  // Routing is now a server-side POST that also flips the session PENDING ->
  // ACTIVE, so the next reviewer to click gets a different scan. The card's own
  // session_id is used only as a fallback if that call fails. If nothing is open
  // anymore the backend returns a null sessionID and we open the panel without
  // switching, rather than dumping the reviewer into a completed review.
  const openingHilRef = useRef(false);
  const handleHilClick = useCallback(async (itemKey, staleSessionId) => {
    if (!openChatSession || openingHilRef.current) return;
    openingHilRef.current = true;
    let sessionId = null;
    try {
      const routed = await routeToNextHilSession();
      sessionId = routed?.sessionID || null;
      // The tile counts PENDING + ACTIVE, so the promotion itself doesn't move
      // it — but a session resolved out from under us does. Keep the card
      // honest without blocking the open.
      if (refreshStats) refreshStats().catch(() => {});
    } catch (err) {
      console.error('Failed to route to next HIL review session:', err);
      // Network hiccup — fall back to the id the card carried so the click
      // still lands somewhere useful.
      sessionId = staleSessionId || null;
    } finally {
      openingHilRef.current = false;
    }
    openChatSession(sessionId || undefined);
  }, [openChatSession, refreshStats]);

  const actionCards = useMemo(
    () => filteredAttention.map(
      (item) => mapAttentionItem(item, navigate, openChatSession, handleTopicClick, handleInterviewClick, handleHilClick),
    ),
    [filteredAttention, navigate, openChatSession, handleTopicClick, handleInterviewClick, handleHilClick],
  );

  const netWorthData = useMemo(() => buildNetWorthTimeline(accountSummaries), [accountSummaries]);

  const currentNetWorth = useMemo(() => {
    if (!netWorthData || netWorthData.length === 0) return null;
    return netWorthData[netWorthData.length - 1].balance;
  }, [netWorthData]);

  const netWorthChange = useMemo(() => {
    if (!netWorthData || netWorthData.length < 2) return null;
    const current = netWorthData[netWorthData.length - 1].balance;
    const previous = netWorthData[netWorthData.length - 2].balance;
    const change = current - previous;
    const pct = previous !== 0 ? (change / Math.abs(previous)) * 100 : null;
    return { change, pct };
  }, [netWorthData]);

  // No customer context — show setup prompt
  if (!userProfile?.hasCustomerContext) {
    return (
      <div className={styles.dashboard}>
        <PageHeader
          title={`${getGreeting()}, ${firstName}`}
          subtitle="Welcome to Livisibly"
        />
        <div className={styles.onboardingCard}>
          <h3 className={styles.onboardingTitle}>Get Started</h3>
          <p className={styles.onboardingDesc}>
            To start managing cases, you'll need to set up your account. You can manage cases
            as an individual or on behalf of an organization.
          </p>
          <button className={styles.onboardingBtn} onClick={() => navigate('/setup_account')}>
            Set Up My Account
          </button>
        </div>
      </div>
    );
  }

  // Has customer but no case
  if (!hasCase) {
    return (
      <div className={styles.dashboard}>
        <PageHeader
          title={`${getGreeting()}, ${firstName}`}
          subtitle="Select or create a case to get started."
        />
        <div className={styles.onboardingCard}>
          <h3 className={styles.onboardingTitle}>
            {userProfile?.isCustomerAdmin() ? 'Create a Case' : 'No Cases Assigned'}
          </h3>
          <p className={styles.onboardingDesc}>
            {userProfile?.isCustomerAdmin()
              ? 'Your account is set up and ready to go. Create a case to start uploading documents and running analysis.'
              : 'You are not currently assigned to any cases. Contact your organization administrator to be assigned to a case.'
            }
          </p>
          {userProfile?.isCustomerAdmin() && (
            <button className={styles.onboardingBtn} onClick={() => navigate('/manage_customer_cases')}>
              Create a Case
            </button>
          )}
        </div>
      </div>
    );
  }

  // Gate the case-having dashboard on case database health and version.
  // If the case DB is unreachable or out of date, CaseDatabaseCheck renders
  // its own status / "contact support" message instead of the dashboard.
  return (
    <CaseDatabaseCheck>
    <div className={styles.dashboard}>
      {/* Greeting */}
      <PageHeader
        title={`${getGreeting()}, ${firstName}`}
        subtitle={`Here's where your case stands today.`}
      />

      {/* Case Stage Pipeline */}
      <div className={styles.pipelineSection}>
        {caseStats ? (
          <StagePipeline stages={stages} />
        ) : (
          <div
            className={`${styles.skeleton} ${styles.skeletonPipeline}`}
            role="status"
            aria-label="Loading case progress"
          />
        )}
      </div>

      {/* Action Cards */}
      {actionCards.length > 0 && (
        <div className={styles.actionsSection}>
          <SectionTitle>Needs Your Attention</SectionTitle>
          <div className={styles.actionGrid}>
            {actionCards.map(({ key, ...card }) => (
              <ActionCard key={key} {...card} />
            ))}
          </div>
        </div>
      )}

      {/* Topics — collapsible list, clickable rows open the agent. Hidden when empty. */}
      {topics.length > 0 && (
        <div className={styles.actionsSection}>
          <TopicsPanel
            topics={topics}
            onTopicClick={handleTopicClick}
            discoveryStatus={discoveryStatus}
          />
        </div>
      )}

      {/* Case Overview Stats — skeleton while the first stats read is in flight */}
      {!caseStats && (
        <div className={styles.statsSection} role="status" aria-label="Loading case overview">
          <SectionTitle>Case Overview</SectionTitle>
          <div className={styles.statsGrid}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={styles.skeletonStatCard} aria-hidden="true">
                <div className={`${styles.skeleton} ${styles.skeletonStatLabel}`} />
                <div className={`${styles.skeleton} ${styles.skeletonStatValue}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Case Overview Stats */}
      {caseStats && (
        <div className={styles.statsSection}>
          <SectionTitle>Case Overview</SectionTitle>
          <div className={styles.statsGrid}>
            <StatCard
              label="Documents"
              value={formatNumber(caseStats.uploaded_file_count)}
            />
            <StatCard
              label="Pages Scanned"
              value={formatNumber(caseStats.scanned_page_count)}
            />
            <StatCard
              label="Transactions"
              value={formatNumber(caseStats.transaction_count)}
            />
            <StatCard
              label="Bank Accounts"
              value={formatNumber(caseStats.account_count)}
            />
          </div>
        </div>
      )}

      {/* Net Worth Trend Chart */}
      {netWorthData && (
        <div className={styles.netWorthSection}>
          <SectionTitle
            actions={
              <>
                {keyDates.length > 0 && (
                  <button
                    className={`${styles.viewAllBtn} ${showKeyDates ? styles.keyDatesToggleActive : ''}`}
                    onClick={() => setShowKeyDates((v) => !v)}
                  >
                    Key Dates
                  </button>
                )}
                <button
                  className={styles.viewAllBtn}
                  onClick={() => navigate('/accounts_net_worth')}
                >
                  View All Accounts
                </button>
              </>
            }
          >
            Net Worth
          </SectionTitle>
          <div className={styles.netWorthCard}>
            <div className={styles.netWorthHeader}>
              <span className={styles.netWorthValue}>
                {formatDollar(currentNetWorth)}
              </span>
              {netWorthChange && (
                <>
                  <span className={`${styles.netWorthChange} ${netWorthChange.change >= 0 ? styles.netWorthUp : styles.netWorthDown}`}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      {netWorthChange.change >= 0
                        ? <polyline points="18,15 12,9 6,15" />
                        : <polyline points="6,9 12,15 18,9" />
                      }
                    </svg>
                    {netWorthChange.change >= 0 ? '+' : ''}{formatDollar(netWorthChange.change)}
                    {netWorthChange.pct != null && ` (${Math.abs(netWorthChange.pct).toFixed(1)}%)`}
                  </span>
                  <span className={styles.netWorthPeriod}>1 period change</span>
                </>
              )}
            </div>
            <div className={styles.netWorthChartWrap}>
              <NetWorthChart dataPoints={netWorthData} height={200} compact id="dash" keyDates={showKeyDates ? keyDates : undefined} />
            </div>
          </div>
        </div>
      )}
    </div>
    </CaseDatabaseCheck>
  );
}
